import React, { useState, useContext } from 'react';
import { ApiContext } from '../App';

const Translate = () => {
  const [file, setFile] = useState(null);
  const [sourceLanguage, setSourceLanguage] = useState('auto');
  const [targetLanguage, setTargetLanguage] = useState('en');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const { authToken } = useContext(ApiContext);
  
  // API base URL
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8787';

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Check file type
      const allowedTypes = ['text/plain', 'application/pdf', 'image/jpeg', 'image/png'];
      if (!allowedTypes.includes(selectedFile.type)) {
        setUploadError('File type not supported. Please upload a PDF, image, or text file.');
        return;
      }
      
      setFile(selectedFile);
      setUploadError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      setUploadError('Please select a file to upload');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setUploadError('');

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('userId', '1'); // Would come from JWT in real app

      // Simulate upload progress
      const interval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(interval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      const response = await fetch(`${API_BASE_URL}/files/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`
        },
        body: formData
      });

      clearInterval(interval);
      setUploadProgress(100);

      if (response.ok) {
        const result = await response.json();
        
        // Now request translation
        const translateResponse = await fetch(`${API_BASE_URL}/translate`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            fileId: result.fileId,
            sourceLanguage,
            targetLanguage
          })
        });

        if (translateResponse.ok) {
          const translateResult = await translateResponse.json();
          setUploadSuccess(true);
          setUploadError('');
          console.log('Translation job ID:', translateResult.jobId);
        } else {
          const error = await translateResponse.json();
          setUploadError(error.error || 'Translation request failed');
        }
      } else {
        const error = await response.json();
        setUploadError(error.error || 'File upload failed');
      }
    } catch (error) {
      setUploadError('An error occurred during upload');
      console.error('Upload error:', error);
    } finally {
      setUploading(false);
    }
  };

  // Language options for translation
  const languages = [
    { code: 'en', name: 'English' },
    { code: 'es', name: 'Spanish' },
    { code: 'fr', name: 'French' },
    { code: 'de', name: 'German' },
    { code: 'it', name: 'Italian' },
    { code: 'pt', name: 'Portuguese' },
    { code: 'ru', name: 'Russian' },
    { code: 'ja', name: 'Japanese' },
    { code: 'ko', name: 'Korean' },
    { code: 'zh', name: 'Chinese' },
    { code: 'ar', name: 'Arabic' },
    { code: 'hi', name: 'Hindi' }
  ];

  return (
    <div className="translate">
      <div className="container">
        <h1>Translate Your Document</h1>
        
        <div className="translate-form-container">
          <form className="translate-form" onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="sourceLanguage">Source Language</label>
                <select
                  id="sourceLanguage"
                  value={sourceLanguage}
                  onChange={(e) => setSourceLanguage(e.target.value)}
                >
                  <option value="auto">Auto-detect</option>
                  {languages.map(lang => (
                    <option key={lang.code} value={lang.code}>{lang.name}</option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label htmlFor="targetLanguage">Target Language</label>
                <select
                  id="targetLanguage"
                  value={targetLanguage}
                  onChange={(e) => setTargetLanguage(e.target.value)}
                >
                  {languages.map(lang => (
                    <option key={lang.code} value={lang.code}>{lang.name}</option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="form-group">
              <label htmlFor="file">Upload Document</label>
              <input
                type="file"
                id="file"
                accept=".pdf,.txt,.doc,.docx,.jpg,.jpeg,.png"
                onChange={handleFileChange}
              />
              {file && <p className="file-info">Selected: {file.name} ({(file.size / 1024).toFixed(2)} KB)</p>}
            </div>
            
            {uploadError && <div className="error-message">{uploadError}</div>}
            
            {uploading && (
              <div className="upload-progress">
                <div className="progress-bar">
                  <div 
                    className="progress-fill" 
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
                <p>Uploading and processing... {uploadProgress}%</p>
              </div>
            )}
            
            {uploadSuccess && (
              <div className="success-message">
                Document uploaded and translation requested successfully!
              </div>
            )}
            
            <button 
              type="submit" 
              className="btn btn-primary" 
              disabled={!file || uploading}
            >
              {uploading ? 'Processing...' : 'Translate Document'}
            </button>
          </form>
          
          <div className="translate-info">
            <h3>Supported File Types</h3>
            <ul>
              <li>PDF Documents</li>
              <li>Text Files (.txt)</li>
              <li>Word Documents (.doc, .docx)</li>
              <li>Images (.jpg, .png)</li>
            </ul>
            
            <h3>Translation Process</h3>
            <ol>
              <li>Upload your document</li>
              <li>Select source and target languages</li>
              <li>We process and translate your document</li>
              <li>Download your translated document</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Translate;